/** SUNTRONIX ONE — ONE MOMENTS.
 *  A restrained, premium celebration overlay for genuine business milestones.
 *  Idempotent (backend marks moments seen), auto-minimises in ~3-4s, respects
 *  reduced-motion / animation-off (shows a static success card instead), and
 *  never blocks the user.  Internal users only.
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, PartyPopper, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { MOMENT_COLORS } from "@/lib/vibe";
import { getPrefs, effectiveAnimation } from "@/lib/experience";

const MomentsContext = createContext({ celebrate: () => {}, refresh: () => {} });
export const useMoments = () => useContext(MomentsContext);

const fmtMoney = (n, cur = "INR") => {
  if (n == null) return null;
  const sym = cur === "INR" ? "\u20B9" : cur === "USD" ? "$" : cur === "EUR" ? "\u20AC" : cur + " ";
  return `${sym}${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
};

function playChime() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const notes = [523.25, 659.25, 783.99]; // C5 E5 G5
    notes.forEach((f, i) => {
      const o = ctx.createOscillator(); const g = ctx.createGain();
      o.type = "sine"; o.frequency.value = f;
      o.connect(g); g.connect(ctx.destination);
      const t = ctx.currentTime + i * 0.09;
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.12, t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.5);
      o.start(t); o.stop(t + 0.55);
    });
    setTimeout(() => ctx.close(), 1200);
  } catch { /* audio not available */ }
}

export function OneMomentsProvider({ enabled = true, children }) {
  const [queue, setQueue] = useState([]);
  const active = queue[0] || null;
  const seenIds = useRef(new Set());

  const enqueue = useCallback((moments) => {
    setQueue((prev) => {
      const known = new Set(prev.map((m) => m.id));
      const fresh = moments.filter((m) => m && m.id && !known.has(m.id) && !seenIds.current.has(m.id));
      return fresh.length ? [...prev, ...fresh] : prev;
    });
  }, []);

  const refresh = useCallback(async () => {
    if (!enabled) return;
    if (!getPrefs().moments) return;
    try {
      const { data } = await api.get("/moments/pending");
      if (data?.items?.length) enqueue(data.items);
    } catch { /* silent */ }
  }, [enabled, enqueue]);

  const celebrate = useCallback((moment) => {
    if (!getPrefs().moments) return;
    if (moment) enqueue([moment]);
    // Also pull anything freshly recorded on the backend.
    setTimeout(refresh, 400);
  }, [enqueue, refresh]);

  useEffect(() => {
    if (!enabled) return;
    refresh();
    const t = setInterval(refresh, 45000);
    const onEvt = () => refresh();
    window.addEventListener("st:moments-refresh", onEvt);
    return () => { clearInterval(t); window.removeEventListener("st:moments-refresh", onEvt); };
  }, [enabled, refresh]);

  const dismiss = useCallback(async (m) => {
    seenIds.current.add(m.id);
    setQueue((prev) => prev.filter((x) => x.id !== m.id));
    try { await api.post(`/moments/${m.id}/seen`); } catch { /* silent */ }
  }, []);

  return (
    <MomentsContext.Provider value={{ celebrate, refresh }}>
      {children}
      {active && <MomentOverlay moment={active} onDismiss={dismiss} />}
    </MomentsContext.Provider>
  );
}

function MomentOverlay({ moment, onDismiss }) {
  const navigate = useNavigate();
  const prefs = getPrefs();
  const anim = effectiveAnimation(prefs);
  const animated = anim === "on";
  const timer = useRef(null);

  useEffect(() => {
    if (prefs.sound && animated) playChime();
    const duration = moment.major ? 4600 : 3800;
    timer.current = setTimeout(() => onDismiss(moment), duration);
    return () => clearTimeout(timer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moment.id]);

  const close = () => { clearTimeout(timer.current); onDismiss(moment); };
  const goAction = (a) => { close(); if (a.route) navigate(a.route); };
  const amount = fmtMoney(moment.amount, moment.currency);

  return (
    <div className="fixed inset-0 z-[200] flex items-start justify-center pt-24 px-4 pointer-events-none"
      role="status" aria-live="polite" data-testid="one-moment">
      {animated && <Confetti major={moment.major} />}
      <div
        data-testid="one-moment-card"
        className={
          "pointer-events-auto relative w-full max-w-md rounded-2xl border border-white/10 " +
          "bg-[#0D1B3D] text-white shadow-2xl overflow-hidden " +
          (animated ? "st-moment-in" : "")
        }
        style={{ boxShadow: "0 24px 60px rgba(8,18,37,0.45)" }}
      >
        <div className="absolute inset-x-0 top-0 h-1"
          style={{ background: "linear-gradient(90deg,#246BFD,#00C8FF,#7657FF,#18D5A4)" }} />
        <button onClick={close} data-testid="one-moment-close"
          className="absolute top-3 right-3 h-8 w-8 grid place-items-center rounded-lg text-white/60 hover:text-white hover:bg-white/10 transition-colors">
          <X className="h-4 w-4" strokeWidth={2} />
        </button>
        <div className="p-6">
          <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-cyan-300">
            <PartyPopper className="h-4 w-4" strokeWidth={2} />
            {moment.major ? "Major Order Won" : (moment.title || "Order Won")}
          </div>
          {moment.customer_name && (
            <div className="mt-3 text-2xl font-bold tracking-tight" data-testid="one-moment-customer"
              style={{ fontFamily: "Manrope, sans-serif" }}>
              {moment.customer_name}
            </div>
          )}
          {amount && (
            <div className="mt-1 text-3xl font-extrabold tracking-tighter text-emerald-300"
              data-testid="one-moment-amount" style={{ fontFamily: "Manrope, sans-serif" }}>
              {amount}
            </div>
          )}
          <p className="mt-2 text-sm text-[#C6D2EA]">
            {moment.subtitle || "Great work. The order is now moving to fulfilment."}
          </p>
          {(moment.next_actions || []).length > 0 && (
            <div className="mt-5 flex flex-wrap gap-2" data-testid="one-moment-actions">
              {moment.next_actions.map((a, i) => (
                <button key={i} onClick={() => goAction(a)}
                  data-testid={`one-moment-action-${i}`}
                  className={
                    "inline-flex items-center gap-1.5 h-9 px-3.5 rounded-lg text-[13px] font-medium transition-colors " +
                    (i === 0
                      ? "bg-[#246BFD] hover:bg-[#1a56db] text-white"
                      : "bg-white/10 hover:bg-white/20 text-white")
                  }>
                  {a.label} <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Confetti({ major }) {
  const count = major ? 70 : 44;
  const pieces = Array.from({ length: count });
  return (
    <div className="fixed inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
      {pieces.map((_, i) => {
        const left = Math.random() * 100;
        const delay = Math.random() * 0.5;
        const dur = 2.2 + Math.random() * 1.6;
        const size = 6 + Math.random() * 6;
        const color = MOMENT_COLORS[i % MOMENT_COLORS.length];
        const rot = Math.random() * 360;
        return (
          <span key={i} className="st-confetti"
            style={{
              left: `${left}%`, width: size, height: size * 0.6,
              background: color, animationDelay: `${delay}s`,
              animationDuration: `${dur}s`, transform: `rotate(${rot}deg)`,
              borderRadius: i % 3 === 0 ? "50%" : "1px",
            }} />
        );
      })}
    </div>
  );
}

export default OneMomentsProvider;
